import React, { useEffect, useId, useState } from "react";
import { createPortal } from "react-dom";
import { useDispatch, useSelector } from "react-redux";
import { Check, Loader2, StickyNote, X } from "lucide-react";
import { fetchDashboardProfile } from "../../redux/dashboardSlice";
import { resetNoteStatus, updateNote } from "../../redux/profileSlice";
import { PROFILE_NOTE_MAX_CHARS } from "../../utils/profileHelpers";

const EditNoteModal = ({ open, onClose, userId, currentNote, onNotify }) => {
  const dispatch = useDispatch();
  const titleId = useId();
  const noteStatus = useSelector((s) => s.profile.noteStatus);
  const saving = noteStatus === "loading";

  const [note, setNote] = useState("");
  const hasNote = Boolean(String(currentNote || "").trim());

  useEffect(() => {
    if (!open) return undefined;

    setNote(currentNote || "");

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const onKeyDown = (event) => {
      if (event.key === "Escape" && !saving) onClose?.();
    };
    window.addEventListener("keydown", onKeyDown);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKeyDown);
      dispatch(resetNoteStatus());
    };
  }, [open, currentNote, dispatch, onClose, saving]);

  if (!open) return null;

  const charCount = note.length;
  const overCap = charCount > PROFILE_NOTE_MAX_CHARS;

  const handleClose = () => {
    if (saving) return;
    onClose?.();
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    const trimmed = note.trim();
    if (!trimmed) {
      onNotify?.("error", "Note required", "Note cannot be empty.");
      return;
    }
    if (trimmed.length > PROFILE_NOTE_MAX_CHARS) {
      onNotify?.(
        "error",
        "Too long",
        `Note cannot exceed ${PROFILE_NOTE_MAX_CHARS} characters.`
      );
      return;
    }
    if (!userId) {
      onNotify?.("error", "Save failed", "Sign in again to update your note.");
      return;
    }

    const result = await dispatch(updateNote({ id: userId, notes: trimmed }));

    if (updateNote.fulfilled.match(result)) {
      await dispatch(fetchDashboardProfile(userId));
      onNotify?.(
        "success",
        "Note saved",
        result.payload?.message || "Your profile note was updated."
      );
      handleClose();
      return;
    }

    onNotify?.("error", "Save failed", String(result.payload || "Try again."));
  };

  return createPortal(
    <div className="prof-note-modal-root" role="presentation">
      <button
        type="button"
        className="prof-note-modal-backdrop"
        aria-label="Close dialog"
        disabled={saving}
        onClick={handleClose}
      />

      <form
        className="prof-note-modal-sheet"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        onSubmit={handleSubmit}
        onClick={(event) => event.stopPropagation()}
      >
        <header className="prof-note-modal-head">
          <span className="prof-note-modal-icon" aria-hidden>
            <StickyNote size={14} />
          </span>
          <div className="prof-note-modal-head-copy">
            <p className="prof-note-modal-eyebrow">Profile</p>
            <h2 id={titleId}>{hasNote ? "Edit note" : "Add note"}</h2>
          </div>
          <button
            type="button"
            className="prof-note-modal-close"
            aria-label="Close"
            disabled={saving}
            onClick={handleClose}
          >
            <X size={14} />
          </button>
        </header>

        <div className="prof-note-modal-body">
          <textarea
            className={`prof-note-modal-textarea${overCap ? " prof-note-modal-textarea-error" : ""}`}
            value={note}
            onChange={(event) => setNote(event.target.value)}
            placeholder="Write something about yourself…"
            rows={5}
            maxLength={PROFILE_NOTE_MAX_CHARS + 1}
            disabled={saving}
            autoFocus={!hasNote}
          />

          <div className="prof-note-modal-meta">
            <span>Visible on your public profile.</span>
            <span className={overCap ? "prof-note-modal-count-error" : ""}>
              {charCount}/{PROFILE_NOTE_MAX_CHARS}
            </span>
          </div>
        </div>

        <footer className="prof-note-modal-actions">
          <button
            type="button"
            className="prof-note-modal-btn prof-note-modal-btn-ghost"
            onClick={handleClose}
            disabled={saving}
          >
            Cancel
          </button>
          <button
            type="submit"
            className="prof-note-modal-btn prof-note-modal-btn-primary"
            disabled={saving || overCap}
          >
            {saving ? (
              <Loader2 size={13} className="animate-spin" aria-hidden />
            ) : (
              <Check size={13} aria-hidden />
            )}
            Save note
          </button>
        </footer>
      </form>
    </div>,
    document.body
  );
};

export default EditNoteModal;
